import { SEND_TRANSPORT, TRANSPORT_PREFERENCE } from '../catalog/link';
import type { SendTransport, TransportPreference } from '../catalog/link';
import { assertNever } from '../../exhaustive';
import { hasTarget } from '../model';
import type { RcuConfig } from '../model';
import { sendDocument } from '../transport/client';
import type { CommandDelivery } from '../transport/client';
import type { RcuDocument } from '../wire';
import { createBridgeClient } from './bridge';
import type { BridgeSettings } from './bridge';
import type { LinkClient, LinkHandlers } from './types';

/**
 * What one send did: which transport carried the command, where, and how many
 * bytes — so "no reply" can be told apart from "never sent".
 */
export interface SendOutcome {
  readonly ok: boolean;
  readonly transport: SendTransport;
  readonly topic: string;
  readonly detail: string;
  readonly bytes: number;
}

/**
 * How a command can leave the tablet right now: a publisher when the gateway
 * session is up, and the topic it publishes to.
 *
 * `null` means there is no session, which is not the same as one that is
 * momentarily down, because a tablet with no gateway still has the device's own
 * HTTP port.
 */
export interface SendRoute {
  readonly publish: ((raw: string) => boolean) | null;
  readonly commandTopic: string;
}

/** What a tablet with no session at all can do: HTTP, or nothing. */
export const NO_SESSION_ROUTE: SendRoute = { publish: null, commandTopic: '' };

export interface LinkSession {
  readonly topics: LinkClient['topics'];
  /** Where this session's commands go while it is open. */
  readonly route: SendRoute;
  close: () => void;
}

/** The pieces a session needs, so a test can open one without a gateway. */
export interface SessionDeps {
  readonly createBridge: (
    settings: BridgeSettings,
    handlers: LinkHandlers,
  ) => Promise<LinkClient>;
  readonly postHttp: (config: RcuConfig, document: RcuDocument) => Promise<CommandDelivery>;
}

export const DEFAULT_SESSION_DEPS: SessionDeps = {
  createBridge: createBridgeClient,
  postHttp: sendDocument,
};

const serialise = (document: RcuDocument): string => JSON.stringify(document);

const bytesOf = (raw: string): number => new TextEncoder().encode(raw).length;

const failed = (transport: SendTransport, detail: string, bytes: number): SendOutcome => ({
  ok: false,
  transport,
  topic: '',
  detail,
  bytes,
});

/**
 * Sends one document over whichever transport the preference allows.
 *
 * This is the only place that decides, so the console and the guest controls
 * cannot drift apart. Automatic walks the device's own port first, because that
 * is the one channel that needs nothing but the RCU, and falls through to the
 * two ways onto the broker when it does not answer. MQTT-only refuses to fall
 * back to HTTP rather than quietly changing channel, which is what makes it
 * useful on a bench, but it does try both broker routes.
 */
export const sendOverRoute = async (
  route: SendRoute,
  config: RcuConfig | null,
  document: RcuDocument,
  preference: TransportPreference,
  postHttp: SessionDeps['postHttp'],
): Promise<SendOutcome> => {
  const raw = serialise(document);
  const bytes = bytesOf(raw);

  /** Over HTTP, the only channel that works without a broker. */
  const post = async (): Promise<SendOutcome> => {
    if (config === null || !hasTarget(config.rcu)) {
      return failed(SEND_TRANSPORT.NONE, 'No RCU address is configured.', bytes);
    }

    const delivery = await postHttp(config, document);
    return {
      ok: delivery.ok,
      transport: SEND_TRANSPORT.HTTP,
      topic: delivery.endpoint,
      detail: delivery.ok
        ? 'Delivered over HTTP; the RCU does not confirm to a browser.'
        : delivery.message,
      bytes,
    };
  };

  /** The one MQTT route: a publish on the gateway session. */
  const broker = (): SendOutcome | null => {
    if (route.publish === null || !route.publish(raw)) return null;
    return {
      ok: true,
      transport: SEND_TRANSPORT.BRIDGE,
      topic: route.commandTopic,
      detail: `Published to ${route.commandTopic} via the bridge.`,
      bytes,
    };
  };

  switch (preference) {
    case TRANSPORT_PREFERENCE.HTTP:
      return post();

    case TRANSPORT_PREFERENCE.MQTT:
      return (
        broker() ??
        failed(
          SEND_TRANSPORT.MQTT,
          'MQTT only, and the bridge has no live session. Switch the transport or fix the link.',
          bytes,
        )
      );

    case TRANSPORT_PREFERENCE.AUTOMATIC: {
      /**
       * A POST the device never answered is worth reporting only if nothing
       * else carries the command: its own message names the address that did
       * not reply, which is more use than a bare "no session".
       */
      const http = await post();
      return http.ok ? http : (broker() ?? http);
    }

    default:
      return assertNever(preference);
  }
};

const sessionOf = (client: LinkClient): LinkSession => ({
  topics: client.topics,
  route: { publish: (raw) => client.publish(raw), commandTopic: client.topics.commands },
  close: () => client.close(),
});

/**
 * Opens the link through the gateway.
 *
 * Returns as soon as the client exists rather than when it is live: coming up is
 * the client's own business and is reported through `handlers.onState`, so a
 * caller that waited for a live socket would sit through a whole connect timeout
 * on a network that is not there.
 */
export const openBridgeSession = async (
  bridge: BridgeSettings,
  handlers: LinkHandlers,
  deps: SessionDeps = DEFAULT_SESSION_DEPS,
): Promise<LinkSession> => sessionOf(await deps.createBridge(bridge, handlers));
